import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type RefObject,
} from "react";
import {
  readVideoPreferences,
  writeVideoPreferences,
} from "@/utils/videoPreferences";
import {
  useBufferedEnd,
  useFullscreen,
  usePictureInPicture,
  usePlaybackRate,
  useVideoElementWiring,
} from "./useMediaElement";
import { isNativeKeyTarget, resolveVideoKeyAction } from "./keymap";
import type { VideoControlBarProps } from "./VideoControlBar";

export interface UseVideoControlsOptions {
  /** Seconds moved by the left/right arrow keys. */
  seekStep?: number;
  /** Seconds moved by Shift+left/right arrow keys. */
  seekStepLarge?: number;
  autoHideDelayMs?: number;
  /**
   * Floor for the seek range when the element's own duration isn't usable —
   * e.g. an HLS EVENT playlist (a partially-encoded recording) that hls.js
   * treats as live and reports as `Infinity`. Mirrors SynchronizedVideoPlayer's
   * `recordingMediaTimeline.mediaDuration` fallback on the session page.
   */
  fallbackDuration?: number | null;
  /**
   * Whether the viewer's persisted playback rate applies. Surfaces without a
   * rate control (the per-file modal) pass false to stay neutral: they play
   * at 1x and never touch the preference.
   */
  persistPlaybackRate?: boolean;
}

/**
 * The slice of VideoControlBar's props that this hook can supply on its own,
 * i.e. everything that is derivable from a plain <video> element. Players with
 * a foreign clock (SynchronizedVideoPlayer, whose time axis is the telemetry
 * session rather than the media) override the time/seek members after
 * spreading.
 */
export type VideoControlsBinding = Pick<
  VideoControlBarProps,
  | "isPlaying"
  | "currentTime"
  | "duration"
  | "bufferedEnd"
  | "volume"
  | "isMuted"
  | "playbackRate"
  | "isFullscreen"
  | "pip"
  | "visible"
  | "onPlayPause"
  | "onSeek"
  | "onSeekStart"
  | "onSeekEnd"
  | "onVolumeChange"
  | "onToggleMute"
  | "onPlaybackRateChange"
  | "onToggleFullscreen"
  | "onTogglePip"
>;

export interface UseVideoControlsResult {
  binding: VideoControlsBinding;
  /**
   * Spread onto the element that wraps both the <video> and the bar. Keyboard
   * transport (onKeyDown + tabIndex) is always included; SessionAnalyticsPage
   * binds its own document-level shortcuts for the analytics surface, and the
   * two coexist through the claimed-event (defaultPrevented) contract rather
   * than an opt-out flag.
   */
  containerProps: {
    onMouseEnter: () => void;
    onMouseMove: () => void;
    onMouseLeave: () => void;
    onKeyDown: (event: ReactKeyboardEvent<HTMLElement>) => void;
    tabIndex: number;
  };
}

/**
 * Default controller for a plain <video>: owns transport state, persists the
 * viewer's volume/mute/rate, and translates control-bar callbacks into
 * element mutations. Knows nothing about HLS — quality is layered on by the
 * caller, which holds the useHlsPlayer instance.
 */
export function useVideoControls(
  videoRef: RefObject<HTMLVideoElement>,
  containerRef: RefObject<HTMLElement>,
  options: UseVideoControlsOptions = {},
): UseVideoControlsResult {
  const {
    seekStep = 2,
    seekStepLarge = 10,
    autoHideDelayMs = 3000,
    fallbackDuration,
    persistPlaybackRate = true,
  } = options;

  const initialPreferences = useRef(readVideoPreferences()).current;

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(initialPreferences.volume);
  const [isMuted, setIsMuted] = useState(initialPreferences.isMuted);
  const [visible, setVisible] = useState(true);

  const bufferedEnd = useBufferedEnd(videoRef);
  const { isFullscreen, toggleFullscreen } = useFullscreen(
    containerRef,
    videoRef,
  );
  const pip = usePictureInPicture(videoRef);
  const { playbackRate, setPlaybackRate } = usePlaybackRate(videoRef, {
    persist: persistPlaybackRate,
  });

  // While the viewer drags the scrubber, `timeupdate` must not yank the thumb
  // back to the element's own (still-catching-up) currentTime.
  const isScrubbingRef = useRef(false);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearHideTimer = useCallback(() => {
    if (hideTimerRef.current !== null) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  const revealControls = useCallback(() => {
    setVisible(true);
    clearHideTimer();
    // Paused, scrubbing, and menu-open states keep the bar pinned; the
    // playing case re-arms the timer on every pointer move.
    if (isPlaying && !isScrubbingRef.current) {
      hideTimerRef.current = setTimeout(() => setVisible(false), autoHideDelayMs);
    }
  }, [autoHideDelayMs, clearHideTimer, isPlaying]);

  useEffect(() => {
    if (!isPlaying) {
      clearHideTimer();
      setVisible(true);
    }
  }, [clearHideTimer, isPlaying]);

  useEffect(() => clearHideTimer, [clearHideTimer]);

  useVideoElementWiring(videoRef, (video) => {
    const syncTime = () => {
      if (!isScrubbingRef.current) {
        setCurrentTime(video.currentTime);
      }
    };
    const syncDuration = () => {
      setDuration(Number.isFinite(video.duration) ? video.duration : 0);
    };
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleVolume = () => {
      setVolume(video.volume);
      setIsMuted(video.muted);
    };
    // A new media resource can reset volume; re-assert the viewer's saved
    // preference each time one loads so a fragment auto-advance doesn't
    // silently drop it. Playback rate is handled by usePlaybackRate.
    const handleLoadedMetadata = () => {
      const preferences = readVideoPreferences();
      video.volume = preferences.volume;
      video.muted = preferences.isMuted;
      syncDuration();
      syncTime();
    };

    video.addEventListener("timeupdate", syncTime);
    video.addEventListener("progress", syncTime);
    video.addEventListener("seeked", syncTime);
    video.addEventListener("durationchange", syncDuration);
    video.addEventListener("loadedmetadata", handleLoadedMetadata);
    video.addEventListener("play", handlePlay);
    video.addEventListener("playing", handlePlay);
    video.addEventListener("pause", handlePause);
    video.addEventListener("volumechange", handleVolume);

    // The element may already be past `loadedmetadata` when wiring attaches
    // (Strict Mode remount, or a source attached by a sibling effect first).
    handleLoadedMetadata();
    // Removing a playing element fires no pause event, so playing state must
    // be re-derived from the element rather than carried over.
    setIsPlaying(!video.paused);

    return () => {
      video.removeEventListener("timeupdate", syncTime);
      video.removeEventListener("progress", syncTime);
      video.removeEventListener("seeked", syncTime);
      video.removeEventListener("durationchange", syncDuration);
      video.removeEventListener("loadedmetadata", handleLoadedMetadata);
      video.removeEventListener("play", handlePlay);
      video.removeEventListener("playing", handlePlay);
      video.removeEventListener("pause", handlePause);
      video.removeEventListener("volumechange", handleVolume);
    };
  });

  const onPlayPause = useCallback(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }
    if (video.paused) {
      // Autoplay/gesture rejections surface as a rejected promise; the pause
      // state is already correct, so there is nothing to roll back.
      void video.play().catch(() => undefined);
    } else {
      video.pause();
    }
  }, [videoRef]);

  const onSeek = useCallback(
    (time: number) => {
      const video = videoRef.current;
      setCurrentTime(time);
      if (video) {
        video.currentTime = time;
      }
    },
    [videoRef],
  );

  const onSeekStart = useCallback(() => {
    isScrubbingRef.current = true;
    clearHideTimer();
    setVisible(true);
  }, [clearHideTimer]);

  const onSeekEnd = useCallback(() => {
    isScrubbingRef.current = false;
    revealControls();
  }, [revealControls]);

  const onVolumeChange = useCallback(
    (nextVolume: number) => {
      const video = videoRef.current;
      // Dragging the slider off zero is an unmute; dragging it to zero mutes.
      const nextMuted = nextVolume === 0;
      if (video) {
        video.volume = nextVolume;
        video.muted = nextMuted;
      }
      setVolume(nextVolume);
      setIsMuted(nextMuted);
      writeVideoPreferences({ volume: nextVolume, isMuted: nextMuted });
    },
    [videoRef],
  );

  const onToggleMute = useCallback(() => {
    const video = videoRef.current;
    const nextMuted = !isMuted;
    if (video) {
      video.muted = nextMuted;
    }
    setIsMuted(nextMuted);
    writeVideoPreferences({ isMuted: nextMuted });
  }, [isMuted, videoRef]);

  const seekBy = useCallback(
    (delta: number) => {
      const video = videoRef.current;
      if (!video) {
        return;
      }
      const limit = Number.isFinite(video.duration)
        ? video.duration
        : Number.POSITIVE_INFINITY;
      onSeek(Math.min(limit, Math.max(0, video.currentTime + delta)));
    },
    [onSeek, videoRef],
  );

  const onKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLElement>) => {
      // A handler closer to the target already claimed this keystroke.
      if (event.defaultPrevented) {
        return;
      }
      const target = event.target as HTMLElement | null;
      // Never swallow keys aimed at native form controls (the bar's range
      // inputs, selects, text fields) — their built-in keyboard handling is
      // the expected behavior once focused.
      if (isNativeKeyTarget(event.target)) {
        return;
      }

      const action = resolveVideoKeyAction(event);
      if (action === null) {
        return;
      }
      // Space on a focused button or link means "activate it"; the transport
      // toggle must not steal the keystroke (e.g. the play button or the
      // modal's transport controls). Arrows and m/f remain available.
      if (
        action === "play-pause" &&
        target &&
        (target.tagName === "BUTTON" || target.tagName === "A")
      ) {
        return;
      }
      // Key auto-repeat would flicker play/pause; one press, one toggle.
      if (event.repeat && action === "play-pause") {
        return;
      }
      event.preventDefault();
      switch (action) {
        case "play-pause":
          onPlayPause();
          break;
        case "seek-back":
          seekBy(-seekStep);
          break;
        case "seek-forward":
          seekBy(seekStep);
          break;
        case "seek-back-large":
          seekBy(-seekStepLarge);
          break;
        case "seek-forward-large":
          seekBy(seekStepLarge);
          break;
        case "toggle-mute":
          onToggleMute();
          break;
        case "toggle-fullscreen":
          toggleFullscreen();
          break;
      }
      revealControls();
    },
    [
      onPlayPause,
      onToggleMute,
      revealControls,
      seekBy,
      seekStep,
      seekStepLarge,
      toggleFullscreen,
    ],
  );

  // The element's duration is the source of truth for the seek range, but a
  // non-finite value (a live/EVENT playlist) leaves the bar disabled. Fall
  // back to a metadata-derived duration so scrubbing stays usable in that
  // case, matching SynchronizedVideoPlayer on the session page.
  const effectiveDuration =
    duration > 0
      ? duration
      : fallbackDuration && fallbackDuration > 0
        ? fallbackDuration
        : 0;

  const binding = useMemo<VideoControlsBinding>(
    () => ({
      isPlaying,
      currentTime,
      duration: effectiveDuration,
      bufferedEnd,
      volume,
      isMuted,
      playbackRate,
      isFullscreen,
      pip: { supported: pip.isSupported, active: pip.isActive },
      visible,
      onPlayPause,
      onSeek,
      onSeekStart,
      onSeekEnd,
      onVolumeChange,
      onToggleMute,
      onPlaybackRateChange: setPlaybackRate,
      onToggleFullscreen: toggleFullscreen,
      onTogglePip: pip.togglePip,
    }),
    [
      bufferedEnd,
      currentTime,
      effectiveDuration,
      isFullscreen,
      isMuted,
      isPlaying,
      onPlayPause,
      onSeek,
      onSeekEnd,
      onSeekStart,
      onToggleMute,
      onVolumeChange,
      pip.isActive,
      pip.isSupported,
      pip.togglePip,
      playbackRate,
      setPlaybackRate,
      toggleFullscreen,
      visible,
      volume,
    ],
  );

  return {
    binding,
    containerProps: {
      onMouseEnter: revealControls,
      onMouseMove: revealControls,
      onMouseLeave: () => {
        if (isPlaying && !isScrubbingRef.current) {
          setVisible(false);
        }
      },
      onKeyDown,
      tabIndex: 0,
    },
  };
}
